import { Alert, Col, Directory, Heading, Icon, Loader, Row, themes } from '@digitools/honest-react';
import { Breadcrumb, Breadcrumbs } from '@digitools/honest-ui';
import { FileType } from '@digitools/honest-react/dist/lib/types/FileType';
import axios from 'axios';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useAuthentication from 'src/hooks/useAuthentication';
import useExperience from 'src/hooks/useExperience';
import useTealium from 'src/hooks/useTealium';
import FileDetailsTable from './FileDetailsTable';
import HelpInformation from './HelpInformation';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { getEnvVariable } from "src/utils/envUtils";
import { useHomePageBreadcrumbs } from 'src/components/Breadcrumbs';
export var toggleSingleOrAll;
(function (toggleSingleOrAll) {
    toggleSingleOrAll["SINGLE"] = "SINGLE";
    toggleSingleOrAll["ALL"] = "ALL";
})(toggleSingleOrAll || (toggleSingleOrAll = {}));
const isEmployerEmailNotificationsEnabled = getEnvVariable('updatedEmployerEmailNotificationsEnabled');
const StandardReportsContainer = () => {
    const authenticationContext = useAuthentication();
    useExperience();
    const { trackEvent, trackView } = useTealium();
    const [downloadError, setDownloadError] = useState(false);
    const [getFileItemsError, setGetFileItemsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fileItems, setFileItems] = useState([]);
    const [selectedDirectory, setSelectedDirectory] = useState(undefined);
    const [toggleTriggered, setToggleTriggered] = useState(false);
    const [toggleError, setToggleError] = useState(false);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    // Use ref to access current fileItems without adding to useCallback dependencies
    const fileItemsRef = useRef([]);
    const selectedDirectoryRef = useRef(undefined);
    const selectAllJustTriggered = useRef(false);
    // Keep refs in sync with state
    useEffect(() => {
        fileItemsRef.current = fileItems;
    }, [fileItems]);
    useEffect(() => {
        selectedDirectoryRef.current = selectedDirectory;
    }, [selectedDirectory]);
    const getFileItems = useCallback(async (filterByDate) => {
        try {
            const url = '/document-service/reports/all';
            const response = await axios.get(url, {
                headers: {
                    Authorization: authenticationContext.authHeader(),
                },
            });
            // Recursively map isSubscribed to checked (only for directories, not files)
            const mapItem = (item) => ({
                ...item,
                // Only set checked for directories (DIR type), not for files
                ...(item.type === 'DIR' && { checked: item.isSubscribed ?? false }),
                children: item.children?.map(mapItem) ?? []
            });
            const transformedData = response.data.map(mapItem);
            // Merge new data with existing data to preserve object references
            // This prevents React from unmounting/remounting components
            const mergeItems = (newItems, existingItems) => {
                if (!existingItems || existingItems.length === 0) {
                    return newItems;
                }
                return newItems.map(newItem => {
                    const existingItem = existingItems.find(e => e.id === newItem.id);
                    if (!existingItem) {
                        return newItem;
                    }
                    // Preserve the existing object reference structure
                    const merged = {
                        ...existingItem,
                        ...newItem,
                        // Recursively merge children
                        children: mergeItems(newItem.children || [], existingItem.children || [])
                    };
                    // If select-all was just triggered, preserve the optimistic local checked state
                    // to prevent it from being overwritten by potentially stale backend data
                    if (selectAllJustTriggered.current && newItem.type === 'DIR' && existingItem.checked !== undefined) {
                        merged.checked = existingItem.checked;
                    }
                    return merged;
                });
            };
            const mergedData = mergeItems(transformedData, fileItemsRef.current);
            // Reset the flag after merge is complete
            selectAllJustTriggered.current = false;
            // Only set selectedDirectory to first item if there isn't one already
            if (!selectedDirectoryRef.current) {
                setSelectedDirectory(mergedData[0]);
            }
            setFileItems(mergedData);
            setGetFileItemsError(false);
            return mergedData;
        }
        catch (error) {
            setGetFileItemsError(true);
            return [];
        }
    }, [authenticationContext]);
    const checkSelectAllState = useCallback(async () => {
        try {
            const response = await axios.get('/document-service/subscriptions', {
                headers: {
                    Authorization: authenticationContext.authHeader(),
                },
            });
            // If the response has an empty subscribedFileItemIds array, all are selected
            const subscribedIds = response.data?.subscribedFileItemIds || [];
            const allSelected = subscribedIds.length === 0;
            setSelectAllChecked(allSelected);
        }
        catch (error) {
            // On 404 or any error, select-all should be off
            setSelectAllChecked(false);
        }
    }, [authenticationContext]);
    useEffect(() => {
        const initializeData = async () => {
            setLoading(true);
            await getFileItems(true);
            await checkSelectAllState();
            setLoading(false);
        };
        initializeData();
    }, [getFileItems, checkSelectAllState]);
    const onSelect = useCallback((fileItem) => {
        setSelectedDirectory(fileItem);
    }, []);
    const handleToggleChange = useCallback(async (checked, fileItem) => {
        try {
            if (checked) {
                await axios.post('/document-service/subscriptions', {
                    scope: toggleSingleOrAll.SINGLE,
                    fileItemId: fileItem.id,
                }, { headers: { Authorization: authenticationContext.authHeader() } });
            }
            else {
                // If turning OFF an individual toggle while select-all is ON, turn off select-all immediately
                if (selectAllChecked) {
                    setSelectAllChecked(false);
                }
                await axios.delete('/document-service/subscriptions/' + fileItem.id, { headers: { Authorization: authenticationContext.authHeader() } });
            }
            if (!toggleTriggered) {
                let eventName = fileItem.label + " ";
                eventName += checked ? 'on' : 'off';
                trackEvent({
                    event_action: TEALIUM_EVENT.EVENT_ACTION.TOGGLE,
                    event_name: eventName,
                    event_type: 'employer reporting',
                    event_version: fileItem.label,
                });
            }
            setToggleTriggered(true);
            setToggleError(false);
            // Re-fetch to sync with backend (someone else might have changed data)
            await getFileItems(true);
            // Check the subscriptions API to verify select-all state
            // This will also handle the case where turning on the last toggle should turn on select-all
            await checkSelectAllState();
            setGetFileItemsError(false);
        }
        catch (error) {
            if (error.response?.status == 404) {
                await getFileItems(true);
                await checkSelectAllState();
                setToggleError(false);
            }
            else {
                setToggleError(true);
                await getFileItems(true);
                await checkSelectAllState();
            }
        }
    }, [authenticationContext, toggleTriggered, trackEvent, getFileItems, checkSelectAllState, selectAllChecked]);
    const handleSelectAllToggle = useCallback(async (checked) => {
        // Set flag to indicate select-all was just triggered
        selectAllJustTriggered.current = true;
        // Optimistically update the select-all checked state
        setSelectAllChecked(checked);
        try {
            if (checked) {
                await axios.post('/document-service/subscriptions', {
                    scope: toggleSingleOrAll.ALL,
                }, { headers: { Authorization: authenticationContext.authHeader() } });
            }
            else {
                await axios.delete('/document-service/subscriptions', { headers: { Authorization: authenticationContext.authHeader() } });
            }
            // Immediately update local state based on the response
            // This ensures the toggle moves right away
            const updateAllDirectories = (items) => {
                return items.map(item => ({
                    ...item,
                    ...(item.type === 'DIR' && { checked }),
                    children: item.children?.length ? updateAllDirectories(item.children) : item.children
                }));
            };
            const updatedItems = updateAllDirectories(fileItemsRef.current);
            setFileItems(updatedItems);
            setToggleTriggered(true);
            setToggleError(false);
            // Re-fetch to get updated subscription states from backend
            // This will sync with any changes made by others
            await getFileItems(true);
            // Check the subscriptions API to get the actual select-all state
            await checkSelectAllState();
            setGetFileItemsError(false);
        }
        catch (error) {
            if (error.response?.status == 404) {
                await getFileItems(true);
                await checkSelectAllState();
                setToggleError(false);
            }
            else {
                setToggleError(true);
                await getFileItems(true);
                await checkSelectAllState();
            }
        }
    }, [authenticationContext, getFileItems, checkSelectAllState]);
    const downloadFile = async (id) => {
        try {
            const response = await axios.get(`/document-service/reports/${id}`, {
                responseType: 'arraybuffer',
                headers: { Authorization: authenticationContext.authHeader() },
            });
            const contentDispositionHeader = response.headers['content-disposition'];
            const contentType = response.headers['content-disposition'];
            const filename = contentDispositionHeader?.split(';')[1]?.split('filename')[1]?.split('=')[1]?.trim();
            const blob = new Blob([response.data], { type: contentType });
            saveAs(blob, filename);
        }
        catch (error) {
            setDownloadError(true);
        }
    };
    const renderGetFileItemsAlert = () => {
        if (getFileItemsError) {
            return (<Alert type='error'>
                    <strong>
                        {"We're sorry. We are unable to retrieve your folders and reports at this time. If this problem persists, please contact technical assistance."}
                    </strong>
                </Alert>);
        }
        else {
            return <></>;
        }
    };
    const renderDownloadErrorAlert = () => {
        if (downloadError) {
            return (<Alert type='error' closeable={true} onClose={() => setDownloadError(false)}>
                    <strong>
                        {"We're sorry. We are unable to download your document at this time. If this problem persists, please contact technical assistance."}
                    </strong>
                </Alert>);
        }
        else {
            return <></>;
        }
    };
    const renderToggleAlert = () => {
        if (toggleError) {
            return (<Alert type='error'>
                    <strong>
                        {"We're sorry. There was an error setting that toggle. If this problem persists, please contact technical assistance."}
                    </strong>
                </Alert>);
        }
        else {
            return <></>;
        }
    };
    const getSelectedDirectory = async (id, items) => {
        items.forEach(fileItem => {
            if (fileItem.id === id) {
                setSelectedDirectory(fileItem);
                return;
            }
            getSelectedDirectory(id, fileItem.children);
        });
    };
    const showAllDocuments = async () => {
        if (selectedDirectory) {
            const selectedDirectoryId = selectedDirectory.id;
            await getSelectedDirectory(selectedDirectoryId, fileItems);
        }
    };
    const formatStartDate = (date) => {
        return format(date, 'yyyy-MM-dd hh:mm:ss');
    };
    const formatEndDate = (date) => {
        const formatted = format(date, 'yyyy-MM-dd');
        return `${formatted} 23:59:59`;
    };
    const onDateSelect = async (startDate, endDate) => {
        try {
            let url = '/document-service/reports/all';
            if (startDate && endDate) {
                url += `?startDate=${formatStartDate(startDate)}`;
                url += `&endDate=${formatEndDate(endDate)}`;
            }
            else if (startDate) {
                url += `?startDate=${formatStartDate(startDate)}`;
            }
            else if (endDate) {
                url += `?endDate=${formatEndDate(endDate)}`;
            }
            const selectedDirectoryId = selectedDirectory.id;
            const response = await axios.get(url, {
                headers: {
                    Authorization: authenticationContext.authHeader(),
                },
            });
            setFileItems(response.data);
            setGetFileItemsError(false);
            await getSelectedDirectory(selectedDirectoryId, response.data);
        }
        catch (error) {
            setGetFileItemsError(true);
        }
    };
    const renderReportsTable = (files) => {
        const filesToDisplay = selectedDirectory ? files : [];
        const label = selectedDirectory ? selectedDirectory.label : 'Select a Directory';
        return (<FileDetailsTable downloadFile={downloadFile} folderName={label} isEmployerNotificationsOn={isEmployerEmailNotificationsEnabled} files={filesToDisplay} filterDocuments={showAllDocuments} trackEvent={trackEvent} trackView={trackView} onDateSelect={onDateSelect}/>);
    };
    const memoizedDirectory = useMemo(() => {
        if (fileItems.length === 0) {
            return null;
        }
        return isEmployerEmailNotificationsEnabled ? (<div>
                <Row style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '1.5rem' }}><b>Reports</b></div>
                    <Row style={{ paddingTop: '.4rem', paddingRight: '.5rem' }}>
                        <Icon name={'email-icon'} color={'primaryDarker'} style={{ paddingTop: '.25rem', paddingRight: '.25rem' }}/>
                        <b style={{ fontSize: '1rem' }}>Notifications</b>
                    </Row>
                </Row>
                <Directory displayType={'toggle'} onItemChange={handleToggleChange} onSelectAllToggle={handleSelectAllToggle} selectAllToggle={true} selectAllMode={'folder'} {...{ selectAllChecked }} directoryItems={fileItems} foldersOnly={true} onItemSelect={onSelect} defaultOpen={true} theme={themes.rebrand}/>
            </div>) : (<Directory title='ALL DOCUMENTS' directoryItems={fileItems} foldersOnly={true} onItemSelect={onSelect} defaultOpen={true} theme={themes.rebrand}/>);
    }, [fileItems, handleToggleChange, handleSelectAllToggle, onSelect, selectAllChecked]);
    const renderDirectoryDetail = useCallback((fileDetailsTable) => {
        if (fileItems.length === 0) {
            return <></>;
        }
        else {
            return (<>
                    <Col lg={'3'} md={'4'} sm={'5'}>
                        {memoizedDirectory}
                    </Col>
                    <Col lg={'9'} md={'8'} sm={'7'} style={{ paddingLeft: isEmployerEmailNotificationsEnabled ? '2rem' : 0 }}>
                        {fileDetailsTable}
                    </Col>
                </>);
        }
    }, [fileItems, memoizedDirectory]);
    const files = useMemo(() => selectedDirectory
        ? selectedDirectory.children.filter(fileItem => fileItem.type !== FileType.DIR)
        : [], [selectedDirectory]);
    return (<div data-public={true}>
            <Breadcrumbs>
                {useHomePageBreadcrumbs()}
                <Breadcrumb>{'Standard reports'}</Breadcrumb>
            </Breadcrumbs>
            <Heading elemType='h1' color={'primaryDarker'}>Standard reports</Heading>
            {loading ? (<div className={'mt-6'}>
                    <Loader isShown={true}/>
                </div>) : (<>
                    {renderGetFileItemsAlert()}
                    {renderDownloadErrorAlert()}
                    {renderToggleAlert()}
                    <br />
                    <Row>{renderDirectoryDetail(renderReportsTable(files))}</Row>
                    <Row>
                        <Col lg={'3'} md={'4'} sm={'5'}/>
                        <Col lg={'9'} md={'8'} sm={'7'}>
                            <HelpInformation trackEvent={trackEvent} trackView={trackView}/>
                        </Col>
                    </Row>
                </>)}
        </div>);
};
export default StandardReportsContainer;
